/**
 * Home-only mobile presentation modes for already-rendered Topic cards.
 * This module deliberately owns no data or card business behaviour: callers
 * continue to render through buildTopicCard and the normal delegated actions.
 */

type LayoutMode = 'snap' | 'stack';

interface AttributeSnapshot {
	role: string | null;
	tabindex: string | null;
	ariaLabel: string | null;
	ariaHidden: string | null;
	stackSlot: string | null;
	inert: boolean;
	children: Array<{ element: HTMLElement; tabindex: string | null; ariaHidden: string | null }>;
}

interface PointerState {
	pointerId: number;
	startX: number;
	startY: number;
	startCard: HTMLElement;
	phase: 'pending' | 'horizontal' | 'vertical';
	captured: boolean;
}

const controllers = new WeakMap<HTMLElement, HomeTopicCollectionController>();
const activeControllers = new Set<HomeTopicCollectionController>();
const MOBILE_QUERY = '(max-width: 640px)';
const CLICK_SUPPRESSION_MS = 420;

export function initializeHomeTopicCollection(container: HTMLElement): void {
	destroyHomeTopicCollection(container);
	const controller = new HomeTopicCollectionController(container);
	controllers.set(container, controller);
	activeControllers.add(controller);
}

export function destroyHomeTopicCollection(container: HTMLElement): void {
	const controller = controllers.get(container);
	if (!controller) return;
	controller.destroy();
	controllers.delete(container);
	activeControllers.delete(controller);
}

export function destroyAllHomeTopicCollections(): void {
	for (const controller of [...activeControllers]) destroyHomeTopicCollection(controller.container);
}

class HomeTopicCollectionController {
	readonly container: HTMLElement;
	private readonly snapshots = new Map<HTMLElement, AttributeSnapshot>();
	private readonly media: MediaQueryList | null;
	private readonly observer: MutationObserver | null;
	private mode: LayoutMode | null = null;
	private activeIndex = 0;
	private pointer: PointerState | null = null;
	private suppressCard: HTMLElement | null = null;
	private suppressTimer: number | null = null;
	private live: HTMLElement | null = null;

	constructor(container: HTMLElement) {
		this.container = container;
		const view = container.ownerDocument.defaultView;
		this.media = view?.matchMedia?.(MOBILE_QUERY) ?? null;
		this.media?.addEventListener('change', this.reconcile);
		this.observer = view?.MutationObserver ? new view.MutationObserver(this.reconcile) : null;
		this.observer?.observe(container, { attributes: true, attributeFilter: ['data-mobile-topic-layout'] });
		container.addEventListener('pointerdown', this.onPointerDown, true);
		container.addEventListener('pointermove', this.onPointerMove, true);
		container.addEventListener('pointerup', this.onPointerUp, true);
		container.addEventListener('pointercancel', this.resetPointer, true);
		container.addEventListener('click', this.onClick, true);
		container.addEventListener('keydown', this.onKeyDown, true);
		this.reconcile();
	}

	destroy = (): void => {
		this.resetPointer();
		this.media?.removeEventListener('change', this.reconcile);
		this.observer?.disconnect();
		this.container.removeEventListener('pointerdown', this.onPointerDown, true);
		this.container.removeEventListener('pointermove', this.onPointerMove, true);
		this.container.removeEventListener('pointerup', this.onPointerUp, true);
		this.container.removeEventListener('pointercancel', this.resetPointer, true);
		this.container.removeEventListener('click', this.onClick, true);
		this.container.removeEventListener('keydown', this.onKeyDown, true);
		this.clearSuppression();
		this.restore();
	};

	private reconcile = (): void => {
		this.resetPointer();
		const declared = this.container.dataset.mobileTopicLayout;
		const nextMode: LayoutMode | null = declared === 'snap' || declared === 'stack' ? declared : null;
		const mobile = this.media?.matches ?? (this.container.ownerDocument.defaultView?.innerWidth ?? 0) <= 640;
		if (!mobile || !nextMode) {
			this.mode = null;
			this.restore();
			return;
		}
		this.mode = nextMode;
		this.container.dataset.mobileTopicLayoutActive = 'true';
		if (nextMode === 'stack') {
			this.captureCards();
			this.activeIndex = Math.min(this.activeIndex, Math.max(0, this.cards().length - 1));
			this.applyStack();
		} else {
			this.restoreStackOnly();
		}
	};

	private cards(): HTMLElement[] {
		return Array.from(this.container.children).filter((element): element is HTMLElement => element.classList.contains('topic-card'));
	}

	private captureCards(): void {
		for (const card of this.cards()) {
			if (this.snapshots.has(card)) continue;
			this.snapshots.set(card, {
				role: card.getAttribute('role'),
				tabindex: card.getAttribute('tabindex'),
				ariaLabel: card.getAttribute('aria-label'),
				ariaHidden: card.getAttribute('aria-hidden'),
				stackSlot: card.getAttribute('data-stack-slot'),
				inert: card.inert,
				children: Array.from(card.querySelectorAll<HTMLElement>('*')).map((element) => ({ element, tabindex: element.getAttribute('tabindex'), ariaHidden: element.getAttribute('aria-hidden') })),
			});
		}
	}

	private restoreAttribute(element: HTMLElement, name: string, value: string | null): void {
		if (value === null) element.removeAttribute(name);
		else element.setAttribute(name, value);
	}

	private restoreStackOnly(): void {
		for (const [card, snapshot] of this.snapshots) {
			this.restoreAttribute(card, 'role', snapshot.role);
			this.restoreAttribute(card, 'tabindex', snapshot.tabindex);
			this.restoreAttribute(card, 'aria-label', snapshot.ariaLabel);
			this.restoreAttribute(card, 'aria-hidden', snapshot.ariaHidden);
			this.restoreAttribute(card, 'data-stack-slot', snapshot.stackSlot);
			card.inert = snapshot.inert;
			for (const child of snapshot.children) {
				this.restoreAttribute(child.element, 'tabindex', child.tabindex);
				this.restoreAttribute(child.element, 'aria-hidden', child.ariaHidden);
			}
		}
		this.snapshots.clear();
		this.live?.remove();
		this.live = null;
	}

	private restore(): void {
		this.restoreStackOnly();
		delete this.container.dataset.mobileTopicLayoutActive;
	}

	private ensureLive(): HTMLElement {
		if (this.live?.isConnected) return this.live;
		const live = this.container.ownerDocument.createElement('span');
		live.className = 'home-topic-stack-live';
		live.setAttribute('aria-live', 'polite');
		live.setAttribute('aria-atomic', 'true');
		this.container.appendChild(live);
		this.live = live;
		return live;
	}

	private applyStack(): void {
		const cards = this.cards();
		if (cards.length === 0) return;
		this.captureCards();
		for (const [index, card] of cards.entries()) {
			const offset = (index - this.activeIndex + cards.length) % cards.length;
			const snapshot = this.snapshots.get(card);
			if (!snapshot) continue;
			if (offset === 0) {
				this.restoreAttribute(card, 'role', snapshot.role);
				this.restoreAttribute(card, 'tabindex', snapshot.tabindex);
				this.restoreAttribute(card, 'aria-label', snapshot.ariaLabel);
				this.restoreAttribute(card, 'aria-hidden', snapshot.ariaHidden);
				card.inert = snapshot.inert;
				for (const child of snapshot.children) {
					this.restoreAttribute(child.element, 'tabindex', child.tabindex);
					this.restoreAttribute(child.element, 'aria-hidden', child.ariaHidden);
				}
				card.dataset.stackSlot = '0';
			} else if (offset <= 2) {
				card.dataset.stackSlot = String(offset);
				card.setAttribute('role', 'button');
				card.tabIndex = 0;
				card.setAttribute('aria-label', `Show topic: ${card.querySelector('h4')?.textContent?.trim() ?? 'topic'}`);
				card.removeAttribute('aria-hidden');
				card.inert = false;
				for (const child of snapshot.children) {
					child.element.setAttribute('aria-hidden', 'true');
					if (child.element.matches('button, a, input, select, textarea, [tabindex]')) child.element.tabIndex = -1;
				}
			} else {
				card.dataset.stackSlot = 'hidden';
				card.setAttribute('aria-hidden', 'true');
				card.tabIndex = -1;
				card.inert = true;
			}
		}
		const active = cards[this.activeIndex];
		this.ensureLive().textContent = active ? `Showing topic: ${active.querySelector('h4')?.textContent?.trim() ?? ''}` : '';
	}

	private advance(delta: number, focus = false): void {
		const cards = this.cards();
		if (cards.length < 2) return;
		this.activeIndex = (this.activeIndex + delta + cards.length) % cards.length;
		this.applyStack();
		if (focus) this.cards()[this.activeIndex]?.focus();
	}

	private isAction(target: EventTarget | null): boolean {
		return typeof (target as { closest?: unknown } | null)?.closest === 'function'
			&& Boolean((target as HTMLElement).closest('.topic-actions, .action-btn, .topic-category-more, [data-proto-click^="toggle"]'));
	}

	private onPointerDown = (event: PointerEvent): void => {
		if (this.mode !== 'stack' || !event.isPrimary || this.isAction(event.target)) return;
		const active = this.container.querySelector<HTMLElement>('.topic-card[data-stack-slot="0"]');
		if (!active || !event.target || !active.contains(event.target as Node)) return;
		this.pointer = { pointerId: event.pointerId, startX: event.clientX, startY: event.clientY, startCard: active, phase: 'pending', captured: false };
	};

	private onPointerMove = (event: PointerEvent): void => {
		const pointer = this.pointer;
		if (!pointer || pointer.pointerId !== event.pointerId) return;
		const dx = event.clientX - pointer.startX;
		const dy = event.clientY - pointer.startY;
		if (pointer.phase === 'pending' && Math.max(Math.abs(dx), Math.abs(dy)) >= 8) {
			pointer.phase = Math.abs(dx) >= Math.abs(dy) * 1.25 ? 'horizontal' : 'vertical';
			if (pointer.phase === 'horizontal' && typeof pointer.startCard.setPointerCapture === 'function') {
				pointer.startCard.setPointerCapture(event.pointerId);
				pointer.captured = true;
			}
		}
		if (pointer.phase === 'horizontal') event.preventDefault();
	};

	private onPointerUp = (event: PointerEvent): void => {
		const pointer = this.pointer;
		if (!pointer || pointer.pointerId !== event.pointerId) return;
		const dx = event.clientX - pointer.startX;
		const dy = event.clientY - pointer.startY;
		this.resetPointer();
		if (this.mode !== 'stack' || pointer.phase !== 'horizontal' || Math.abs(dx) < 48 || Math.abs(dx) < Math.abs(dy) * 1.25) return;
		this.suppressNextClick(pointer.startCard);
		this.advance(dx < 0 ? 1 : -1);
	};

	private resetPointer = (): void => {
		const pointer = this.pointer;
		if (pointer?.captured && pointer.startCard.hasPointerCapture?.(pointer.pointerId)) pointer.startCard.releasePointerCapture(pointer.pointerId);
		this.pointer = null;
	};

	private suppressNextClick(card: HTMLElement): void {
		this.clearSuppression();
		this.suppressCard = card;
		this.suppressTimer = this.container.ownerDocument.defaultView?.setTimeout(() => this.clearSuppression(), CLICK_SUPPRESSION_MS) ?? null;
	}

	private clearSuppression(): void {
		if (this.suppressTimer !== null) this.container.ownerDocument.defaultView?.clearTimeout(this.suppressTimer);
		this.suppressTimer = null;
		this.suppressCard = null;
	}

	private onClick = (event: MouseEvent): void => {
		if (this.mode !== 'stack') return;
		if (this.isAction(event.target)) return;
		const suppressed = this.suppressCard;
		if (suppressed && event.target && suppressed.contains(event.target as Node)) {
			this.clearSuppression();
			event.preventDefault();
			event.stopImmediatePropagation();
			return;
		}
		const preview = typeof (event.target as { closest?: unknown } | null)?.closest === 'function'
			? (event.target as HTMLElement).closest<HTMLElement>('.topic-card[data-stack-slot="1"], .topic-card[data-stack-slot="2"]')
			: null;
		if (!preview) return;
		const slot = Number(preview.dataset.stackSlot);
		event.preventDefault();
		event.stopImmediatePropagation();
		this.advance(slot);
	};

	private onKeyDown = (event: KeyboardEvent): void => {
		if (this.mode !== 'stack' || (event.key !== 'Enter' && event.key !== ' ')) return;
		const preview = typeof (event.target as { closest?: unknown } | null)?.closest === 'function'
			? (event.target as HTMLElement).closest<HTMLElement>('.topic-card[data-stack-slot="1"], .topic-card[data-stack-slot="2"]')
			: null;
		if (!preview) return;
		event.preventDefault();
		event.stopImmediatePropagation();
		this.advance(Number(preview.dataset.stackSlot), true);
	};
}
